import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Bookmark, Trash2 } from 'lucide-react';
import { SEARCH_TYPES, deleteSavedView, listSavedViews, saveView, type SearchType } from '@mkcore/platform/schema';
import { Button, Card, EmptyState, ErrorState, Input, PageHeader, Skeleton } from '@mkcore/ui';
import { call } from '@/lib/api';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { HitStatus, searchIcons, useDebounced, useOpenHit, useSearch } from '@/features/search/search';

const search = z.object({
  q: z.string().optional(),
  /** Kinds to search, comma-separated; all when left out. */
  types: z.string().optional(),
});
export const Route = createFileRoute('/_app/search')({ validateSearch: search, component: SearchPage });

const typesOf = (csv: string | undefined): SearchType[] => (csv ? csv.split(',').filter((t): t is SearchType => (SEARCH_TYPES as readonly string[]).includes(t)) : []);

/**
 * The global search (CO-0028): one query over clients, contacts, projects, engagements, your hours, invoices and
 * issues, each with the permissions of its own list; kinds to filter on, and searches saved by name.
 */
function SearchPage() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: '/search' });
  const message = useErrorMessage();
  const open = useOpenHit();
  const [text, setText] = useState(s.q ?? '');
  const debounced = useDebounced(text, 300);
  const types = typesOf(s.types);
  // Typing goes to the URL after a pause; a URL that changes otherwise (a saved search, back) goes to the field.
  // pushed is what the URL last got, so neither side overwrites the other.
  const pushed = useRef(s.q ?? '');
  useEffect(() => {
    if (debounced === pushed.current) return;
    pushed.current = debounced;
    void navigate({ search: (p) => ({ ...p, q: debounced || undefined }), replace: true });
  }, [debounced, navigate]);
  useEffect(() => {
    if ((s.q ?? '') === pushed.current) return;
    pushed.current = s.q ?? '';
    setText(s.q ?? '');
  }, [s.q]);
  const q = s.q ?? '';
  const res = useSearch(q, { limit: 20, types });
  const saved = useQuery({ queryKey: ['admin', 'savedView', 'search'], queryFn: () => call(listSavedViews, { listKey: 'search' }) });
  const save = useActionMutation(saveView, { invalidate: [['admin', 'savedView']], success: t('search.savedDone') });
  const remove = useActionMutation(deleteSavedView, { invalidate: [['admin', 'savedView']] });
  const [name, setName] = useState<string | null>(null);
  const toggle = (type: SearchType) => {
    const next = types.includes(type) ? types.filter((x) => x !== type) : [...types, type];
    void navigate({ search: (p) => ({ ...p, types: next.length ? next.join(',') : undefined }), replace: true });
  };

  return (
    <>
      <PageHeader title={t('search.title')} description={t('search.description')} />
      <div className="grid gap-4 lg:grid-cols-[1fr_16rem]">
        <div className="grid content-start gap-4">
          <Input type="search" aria-label={t('search.field')} placeholder={t('search.placeholder')} value={text} onChange={(e) => setText(e.target.value)} autoFocus className="h-11 text-base" />
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('search.kinds')}>
            {SEARCH_TYPES.map((type) => (
              <Button key={type} size="sm" variant={types.includes(type) ? 'primary' : 'secondary'} aria-pressed={types.includes(type)} onClick={() => toggle(type)}>
                {searchIcons[type]} {t(`search.type.${type}`)}
              </Button>
            ))}
          </div>
          {q.trim().length < 2 ? (
            <p className="text-sm text-fg-3">{t('search.minLength')}</p>
          ) : res.isError ? (
            <ErrorState title={t('errors.generic')} description={message(res.error)} />
          ) : !res.data ? (
            <Skeleton className="h-48" />
          ) : (
            <>
              {res.data.failed.length > 0 && (
                <p role="status" className="text-sm text-warning">
                  {t('search.failed', { kinds: res.data.failed.map((k) => t(`search.type.${k}`)).join(', ') })}
                </p>
              )}
              {res.data.groups.length === 0 ? (
                <EmptyState title={t('search.none', { q })} />
              ) : (
                res.data.groups.map((g) => (
                  <section key={g.type} aria-labelledby={`search-${g.type}`} className="grid gap-1">
                    <h2 id={`search-${g.type}`} className="flex items-center gap-2 text-sm font-bold text-fg-2 [&_svg]:size-4">
                      {searchIcons[g.type]} {t(`search.type.${g.type}`)} <span className="font-normal text-fg-3">{g.items.length}</span>
                    </h2>
                    <Card className="divide-y divide-line-subtle">
                      {g.items.map((hit) => (
                        <a key={hit.id} href={hit.path} onClick={(e) => open(hit.path, e)} className="flex items-center gap-3 px-3 py-2 hover:bg-sunken">
                          <span className="grid min-w-0 flex-1">
                            <span className="truncate font-bold">{hit.title}</span>
                            {hit.subtitle && <span className="truncate text-xs text-fg-2">{hit.subtitle}</span>}
                          </span>
                          <HitStatus hit={hit} />
                        </a>
                      ))}
                    </Card>
                  </section>
                ))
              )}
            </>
          )}
        </div>
        <aside className="grid content-start gap-2 text-sm">
          <h2 className="text-xs font-bold uppercase text-fg-3">{t('search.saved')}</h2>
          {saved.data?.items.length ? (
            <ul className="grid gap-1">
              {saved.data.items.map((v) => (
                <li key={v.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    className="flex-1 truncate rounded px-1 py-0.5 text-left hover:bg-sunken"
                    onClick={() =>
                      void navigate({ search: { q: typeof v.params.q === 'string' ? v.params.q : undefined, types: Array.isArray(v.params.types) && v.params.types.length ? v.params.types.join(',') : undefined } })
                    }
                  >
                    {v.name}
                  </button>
                  <Button size="icon" variant="ghost" aria-label={t('search.deleteSaved', { name: v.name })} disabled={remove.isPending} onClick={() => remove.mutate({ input: { id: v.id } })}>
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-fg-3">{t('search.noSaved')}</p>
          )}
          {q.trim().length >= 2 &&
            (name === null ? (
              <Button variant="secondary" size="sm" onClick={() => setName(q.trim())}>
                <Bookmark /> {t('search.save')}
              </Button>
            ) : (
              <form
                className="grid gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  save.mutate({ input: { listKey: 'search', name: name.trim(), params: { q: q.trim(), ...(types.length && { types }) } } }, { onSuccess: () => setName(null) });
                }}
              >
                <Input aria-label={t('search.saveName')} value={name} onChange={(e) => setName(e.target.value)} autoFocus className="h-8" />
                <div className="flex gap-2">
                  <Button type="submit" size="sm" loading={save.isPending} disabled={!name.trim()}>
                    {t('common.save')}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setName(null)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </form>
            ))}
        </aside>
      </div>
    </>
  );
}
